/**
 * Unified order status constants
 */
export const UNIFIED_ORDER_STATUS = {
  NEW: "NEW",
  PREPARING: "PREPARING",
  READY: "READY",
  PICKEDUP: "PICKEDUP",
  DELIVERED: "DELIVERED",
  CANCELLED: "CANCELLED",
  UNKNOWN: "UNKNOWN",
};

export const UNIFIED_ORDER_STATUS_LEVEL = {
  NEW: 1,
  PREPARING: 2,
  READY: 3,
  PICKEDUP: 4,
  DELIVERED: 5,
  CANCELLED: 6,
  UNKNOWN: 7,
};

export const UNIFIED_ORDER_STATUS_LEVEL_MAP = {
  1: UNIFIED_ORDER_STATUS.NEW,
  2: UNIFIED_ORDER_STATUS.PREPARING,
  3: UNIFIED_ORDER_STATUS.READY,
  4: UNIFIED_ORDER_STATUS.PICKEDUP,
  5: UNIFIED_ORDER_STATUS.DELIVERED,
  6: UNIFIED_ORDER_STATUS.CANCELLED,
  7: UNIFIED_ORDER_STATUS.UNKNOWN,
};

/**
 * Unified payment status constants
 */
export const UNIFIED_PAYMENT_STATUS = {
  PAID: "PAID",
  PENDING: "pending",
  FAILED: "FAILED",
  REFUNDED: "REFUNDED",
};

/**
 * Unified order type constants
 */
export const UNIFIED_ORDER_TYPE = {
  PHYSICAL: "physical",
  PARTNER: "partner",
  DINING: "Dining",
  DELIVERY: "delivery",
  TAKEAWAY: "Takeaway",
};

/**
 * Unified delivery status constants
 */
export const UNIFIED_DELIVERY_STATUS = {
  UNASSIGNED: "UNASSIGNED",
  ASSIGNED: "ASSIGNED",
  ARRIVED: "ARRIVED",
  PICKED: "PICKED",
  DELIVERED: "DELIVERED",
  CANCELLED: "CANCELLED",
};

/**
 * @typedef {Object} UnifiedAddress
 * @property {string} text - Full address text
 * @property {Object} location - Coordinates
 * @property {number} location.lat - Latitude
 * @property {number} location.lng - Longitude
 */

/**
 * @typedef {Object} UnifiedCustomer
 * @property {string} name - Customer name
 * @property {string} phone - Customer phone number
 * @property {UnifiedAddress} address - Customer address
 * @property {string} instructions - Delivery instructions
 */

/**
 * @typedef {Object} UnifiedPrice
 * @property {number} base - Base price before discounts
 * @property {number} final - Final price after discounts
 * @property {number} discount - Discount amount
 * @property {number} tax - Tax amount
 * @property {number} packingCharge - Packing charge if any
 */

/**
 * @typedef {Object} UnifiedAddon
 * @property {string} name - Addon name
 * @property {number} price - Addon price
 */

/**
 * @typedef {Object} UnifiedVariant
 * @property {string} id - Variant ID
 * @property {string} name - Variant name
 * @property {string} groupName - Variant group name
 * @property {number} price - Variant price
 */

/**
 * @typedef {Object} UnifiedDeliveryPerson
 * @property {string} name - Delivery person name
 * @property {string} phone - Delivery person phone
 * @property {string} [image] - Delivery person image URL
 */

/**
 * @typedef {Object} UnifiedDelivery
 * @property {UnifiedDeliveryPerson} person - Delivery person details
 * @property {string} status - Delivery status
 * @property {number|null} eta - Estimated time of arrival in minutes
 * @property {number|string} distance - Delivery distance in kilometers
 */

/**
 * @typedef {Object} UnifiedBillTxnLog
 * @property {number} beforeDiscountTotal - Total before discount
 * @property {number} cgst - CGST amount
 * @property {string} cgstPercent - CGST percentage
 * @property {number} discount - Discount amount
 * @property {number} discountFlat - Flat discount amount
 * @property {number} discountPercent - Discount percentage
 * @property {boolean} gst - GST applicable
 * @property {boolean} gstExcluded - GST excluded
 * @property {string} gstNo - GST number
 * @property {boolean} service - Service charge applicable
 * @property {number} sgst - SGST amount
 * @property {string} sgstPercent - SGST percentage
 * @property {number} subTotal - Subtotal amount
 * @property {number} tip - Tip amount
 * @property {number} total - Total amount
 * @property {string} paymentType - Payment type
 */

/**
 * @typedef {Object} UnifiedOrderItem
 * @property {string} id - Item ID
 * @property {string} itemMenuId - Menu item ID
 * @property {string} feUniqueItemId - Frontend unique item ID
 * @property {string} name - Item name
 * @property {number} quantity - Quantity ordered
 * @property {string} price - Item price
 * @property {string} variantName - Variant name if applicable
 * @property {string|number} variantId - Variant ID if applicable
 * @property {boolean} isVariant - Whether item has variants
 * @property {boolean} isExtra - Whether item is an extra
 * @property {string} itemPreparationNote - Preparation notes
 * @property {string} itemCancelReason - Cancellation reason if applicable
 * @property {string} status - Item status
 * @property {boolean} isCancelled - Whether item is cancelled
 * @property {string} createdAt - Creation timestamp
 * @property {string} updatedAt - Last update timestamp
 * @property {UnifiedAddon[]} [addons] - Addons if applicable
 */

/**
 * @typedef {Object} UnifiedPaymentAmount
 * @property {number} total - Total amount
 * @property {number} subtotal - Subtotal before tax and delivery
 * @property {number} discount - Total discount
 * @property {number} tax - Total tax
 * @property {number} deliveryCharge - Delivery charge
 */

/**
 * @typedef {Object} UnifiedPayment
 * @property {string} method - Payment method
 * @property {string} status - Payment status
 * @property {UnifiedPaymentAmount} amount - Payment amounts
 */

/**
 * @typedef {Object} UnifiedRestaurant
 * @property {string} id - Restaurant ID
 * @property {Object} location - Restaurant location
 * @property {number} location.lat - Latitude
 * @property {number} location.lng - Longitude
 */

/**
 * @typedef {Object} UnifiedOrderMeta
 * @property {number} prepTimePredicted - Predicted prep time in minutes
 * @property {string} orderedTime - Time when order was placed
 * @property {string|null} placedTime - Time when restaurant accepted the order
 * @property {string} placingState - Order placing state (e.g., WITH_RELAYER)
 * @property {number} earlyMfrTime - Time in seconds for early food readiness
 * @property {number} foodHandoverTimeSec - Expected handover time in seconds
 * @property {string[]} discountDescriptions - Descriptions of applied discounts
 */

/**
 * @typedef {Object} UnifiedOrder
 * @property {string} orderId - Order ID
 * @property {string} partnerId - Partner identifier (for online orders)
 * @property {string} partnerOrderId - Partner's order ID
 * @property {string} partnerName - Partner vendor name (e.g., Swiggy)
 * @property {string} billNo - Bill number
 * @property {string} staffId - Staff ID (NA for online orders)
 * @property {string} tableVPA - Table VPA (NA for online orders)
 * @property {string} orderType - Order type (e.g., 'online')
 * @property {string} typeOfOrder - Type of order (Dining/Delivery/Takeaway)
 * @property {string} orderStatus - Order status
 * @property {string} billStatus - Bill status
 * @property {number} amount - Total bill amount
 * @property {string} paymentmethod - Payment method
 * @property {Array} partialAmoutsplit - Partial amount splits
 * @property {UnifiedBillTxnLog} billTxnLogs - Detailed bill transaction data
 * @property {number} discount - Discount amount
 * @property {number} tips - Tips
 * @property {string} orderPreparationNote - Notes related to order prep
 * @property {string} createdAt - Order creation timestamp
 * @property {string} updatedAt - Order last updated timestamp
 * @property {boolean} canceled - Whether the order is cancelled
 * @property {string} cancelReason - Reason for cancellation
 * @property {string} orderOTP - Order OTP
 * @property {UnifiedCustomer} customer - Customer information
 * @property {UnifiedDelivery} delivery - Delivery details
 * @property {UnifiedOrderItem[]} items - List of ordered items
 * @property {UnifiedRestaurant} restaurant - Restaurant details
 * @property {UnifiedOrderMeta} meta - Operational metadata
 * @property {Object} rawOrder - Raw partner order payload for audit/debug
 */
